import { useEffect, useRef, type ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { LW_DS_CLASS } from './LwThemeScope';
import { useScrollLock, useWindowEscapeFallback } from './hooks';

/** The caller's return target while it is on the page, else what had focus when the modal opened. */
function returnFocusTarget(preferred: HTMLElement | null | undefined, opened: HTMLElement | null): HTMLElement | null {
  const target = preferred && document.contains(preferred) ? preferred : opened;
  return target && document.contains(target) ? target : null;
}

/**
 * Modal card — gold eyebrow + serif title + optional sub, body, and a footer
 * action row, styled by the kit's `.modal-*` rules. Closes on Escape (topmost
 * only), a backdrop click, and the always-present header close button.
 * `size='tall'` is the full-height editor layout. Focus returns to the element
 * focused when it opened, or to `returnFocusTo` (as in LwConfirmDialog) when
 * a menu that opened it has already given focus up.
 */
export function LwModal({
  eyebrow,
  title,
  sub,
  children,
  footer,
  size,
  className,
  containerClassName,
  returnFocusTo,
  onClose
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'default' | 'wide' | 'tall';
  className?: string;
  containerClassName?: string;
  returnFocusTo?: HTMLElement | null;
  onClose: () => void;
}) {
  useScrollLock();
  const contentRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(
    typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null
  );
  const returnFocusToRef = useRef(returnFocusTo);
  returnFocusToRef.current = returnFocusTo;
  useWindowEscapeFallback(contentRef, onClose);
  useEffect(() => () => {
    returnFocusTarget(returnFocusToRef.current, returnFocusRef.current)?.focus();
  }, []);
  const sizeClass = size === 'wide' ? ' wide' : size === 'tall' ? ' tall' : '';

  return (
    <DialogPrimitive.Root
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogPrimitive.Portal>
        <div className={LW_DS_CLASS}>
          <div
            className={`modal${containerClassName ? ` ${containerClassName}` : ''}`}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                onClose();
              }
            }}
          >
            <DialogPrimitive.Overlay
              className="modal-backdrop"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  onClose();
                }
              }}
            />
            <DialogPrimitive.Content
              asChild
              id={undefined}
              {...(!sub ? { 'aria-describedby': undefined } : {})}
              onOpenAutoFocus={(event) => {
                event.preventDefault();
                contentRef.current?.focus();
              }}
              onCloseAutoFocus={(event) => {
                event.preventDefault();
                returnFocusTarget(returnFocusToRef.current, returnFocusRef.current)?.focus();
              }}
              onPointerDownOutside={(event) => event.preventDefault()}
            >
              <div
                ref={contentRef}
                tabIndex={-1}
                className={`modal-card${sizeClass}${className ? ` ${className}` : ''}`}
                data-lw-overlay-layer=""
              >
                <DialogPrimitive.Close asChild>
                  <button className="modal-x" aria-label="Close" type="button">
                    <X />
                  </button>
                </DialogPrimitive.Close>
                <div className="modal-head">
                  {eyebrow && <div className="modal-eyebrow">{eyebrow}</div>}
                  <DialogPrimitive.Title asChild>
                    <div className="modal-title">{title}</div>
                  </DialogPrimitive.Title>
                  {sub && (
                    <DialogPrimitive.Description asChild>
                      <div className="modal-sub">{sub}</div>
                    </DialogPrimitive.Description>
                  )}
                </div>
                <div className="modal-body">{children}</div>
                {footer && <div className="modal-foot">{footer}</div>}
              </div>
            </DialogPrimitive.Content>
          </div>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
